import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, User, Mail, Phone, Building, MessageSquare,
  Send, CheckCircle, PawPrint, Star, ArrowRight, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { sendDemoRequestEmail } from '../services/emailService';
import { useSeo } from '../utils/useSeo';

const DemoRequestPage: React.FC = () => {
  useSeo({
    title: 'Ücretsiz Demo Talep Edin | KlinikVet',
    description: 'KlinikVet veteriner kliniği yönetim sistemini ücretsiz deneyin. Formu doldurun, sizi arayalım.',
    path: '/demo-request',
  });
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', clinic: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toast.error('Geçerli bir email adresi girin');
      return;
    }
    setIsSubmitting(true);
    try {
      const requestData = {
        ...formData,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        timestamp: new Date().toISOString(),
        id: Date.now(),
      };
      const sent = await sendDemoRequestEmail(requestData);
      if (!sent) {
        // Talep bize ulaşmadı: başarı ekranı GÖSTERME, kullanıcıyı bilgilendir
        toast.error('Talebiniz iletilemedi. Lütfen tekrar deneyin veya bize e-posta ile ulaşın.');
        return;
      }
      setSubmitted(true);
    } catch {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] antialiased">
      {/* Navbar */}
      <nav className="bg-white border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Ana sayfa
            </Link>
            <span className="w-px h-4 bg-slate-200" />
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-slate-900 flex items-center justify-center">
                <PawPrint className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-[14px] text-slate-900">KlinikVet</span>
            </Link>
          </div>
          <Link to="/demo" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors">
            Demo hesabı →
          </Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        {submitted ? (
          /* ── Teşekkür ekranı ── */
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-[28px] font-bold text-slate-900 mb-3 tracking-tight">Talebiniz alındı!</h2>
            <p className="text-[15px] text-slate-600 leading-relaxed mb-8">
              En kısa sürede size dönüş yapacağız. Beklerken uygulamayı demo hesabıyla inceleyebilirsiniz.
            </p>
            <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
              <Link to="/demo"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-[14px] font-semibold hover:bg-slate-800 transition-colors">
                Demoyu Gez
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-[14px] font-semibold hover:border-slate-300 transition-colors">
                Ana Sayfaya Dön
              </Link>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Sol: Form */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700 mb-3">Demo Talebi</p>
              <h1 className="text-[36px] md:text-[44px] font-bold tracking-[-0.03em] leading-[1.05] text-slate-900 mb-4">
                Klinik yönetimini
                <br />
                <span className="italic font-serif font-normal text-slate-500">birlikte keşfedelim.</span>
              </h1>
              <p className="text-[15px] text-slate-600 leading-relaxed mb-8">
                Formu doldurun, size özel bir demo ayarlayalım. İster canlı gösterim, ister deneme hesabı — ikiniz de ücretsiz.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* İsim + Klinik */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">
                      Ad Soyad <span className="text-rose-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" name="name" value={formData.name} onChange={handleChange}
                        placeholder="Dr. Adınız Soyadınız" required
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">Klinik Adı</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input type="text" name="clinic" value={formData.clinic} onChange={handleChange}
                        placeholder="Klinik adı (opsiyonel)"
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors" />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">
                    Email <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                      placeholder="ornek@klinik.com" required
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors" />
                  </div>
                </div>

                {/* Telefon */}
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">
                    Telefon <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      placeholder="0532 123 45 67" required
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors" />
                  </div>
                </div>

                {/* Mesaj */}
                <div className="space-y-1.5">
                  <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wide">
                    Mesaj <span className="text-slate-400 font-normal normal-case">(opsiyonel)</span>
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <textarea name="message" value={formData.message} onChange={handleChange}
                      rows={4} placeholder="Özel ihtiyaçlarınız, sorularınız..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-colors resize-none" />
                  </div>
                </div>

                <button type="submit" disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-semibold transition-colors">
                  {isSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Gönderiliyor...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Demo Talebi Gönder</>
                  )}
                </button>

                <p className="text-[11.5px] text-slate-400 text-center">
                  En geç 24 saat içinde dönüş yapılır. Spam yok, asla.
                </p>
              </form>
            </motion.div>

            {/* Sağ: Social proof */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="space-y-5 lg:pt-20">
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: '120+', l: 'Aktif klinik' },
                  { v: '%98', l: 'Memnuniyet' },
                  { v: '25K+', l: 'Kayıtlı hasta' },
                  { v: '2dk', l: 'Kurulum süresi' },
                ].map(s => (
                  <div key={s.l} className="p-4 rounded-2xl bg-white border border-slate-200 text-center">
                    <div className="text-[26px] font-bold text-slate-900 leading-none">{s.v}</div>
                    <div className="text-[11px] text-slate-500 mt-1.5 font-medium">{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Testimonials */}
              <div className="space-y-3">
                {[
                  { quote: 'Eskiden defter tutuyorduk, şimdi her şey parmaklarımızın ucunda.', name: 'Dr. Mehmet Yılmaz', role: 'İstanbul', grad: 'from-teal-500 to-cyan-600' },
                  { quote: 'Aşı kartı PDF çıktısı çok profesyonel. Müşterilerimiz bayıldı!', name: 'Vet. Ayşe Demir', role: 'Ankara', grad: 'from-purple-500 to-pink-600' },
                ].map((t, i) => (
                  <div key={i} className="p-5 rounded-2xl bg-white border border-slate-200">
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <Star key={j} className="w-3 h-3 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <p className="text-[13px] text-slate-700 leading-relaxed mb-4">"{t.quote}"</p>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${t.grad} flex items-center justify-center text-white text-[11px] font-bold`}>
                        {t.name.split(' ').slice(-2).map(w => w[0]).join('')}
                      </div>
                      <div>
                        <p className="text-[12px] font-bold text-slate-900">{t.name}</p>
                        <p className="text-[11px] text-slate-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Checklist */}
              <div className="p-5 rounded-2xl bg-teal-50 border border-teal-200">
                <p className="text-[12px] font-bold text-teal-800 uppercase tracking-wider mb-3">Demo'da görecekleriniz</p>
                <ul className="space-y-2">
                  {['Hasta ve sahip yönetimi', 'Randevu takvimi', 'Aşı kartı & PDF çıktısı', 'Mobil aile uygulaması', 'İstatistik & raporlar'].map(item => (
                    <li key={item} className="flex items-center gap-2 text-[13px] text-teal-900">
                      <CheckCircle className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoRequestPage;
